import { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import useAuth from "./useAuth";
import { useGetVerifyPlansMutation } from "./billing";
import SecureStorage from "../utils/secureStorage";

interface PaymentStatus {
  customer_plans: string[];
  customer_top_plan: string | null;
  is_new_user: boolean;
  trial_left_days: number;
  lastPaymentCheck: number;
  borrows_owner_plan?: boolean;
  owner_plans?: string[];
  owner_top_plan?: string;
}

interface PaymentVerificationHook {
  isVerifying: boolean;
  verifyAndUpdateStatus: () => Promise<void>;
  forcePaymentCheck: () => Promise<void>;
  isPaymentStatusStale: () => boolean;
  getPaymentStatus: () => PaymentStatus | null;
}

const PAYMENT_CHECK_INTERVAL = 3600000;

export const usePaymentVerification = (): PaymentVerificationHook => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const auth = useAuth();
  const [isVerifying, setIsVerifying] = useState(false);

  const { mutateAsync: verifyPaymentMutation } = useGetVerifyPlansMutation();

  const isPaymentStatusStale = useCallback((): boolean => {
    const sessionData = SecureStorage.getAuthServiceData();
    if (!sessionData) return true;

    try {
      const { lastPaymentCheck } = sessionData;
      if (!lastPaymentCheck) return true;

      const timeDiff = Date.now() - lastPaymentCheck;
      return timeDiff > PAYMENT_CHECK_INTERVAL;
    } catch {
      return true;
    }
  }, []);

  const getPaymentStatus = useCallback((): PaymentStatus | null => {
    const sessionData = SecureStorage.getAuthServiceData();
    if (!sessionData) return null;

    try {
      return {
        customer_plans: sessionData.customer_plans || [],
        customer_top_plan: sessionData.customer_top_plan || null,
        is_new_user: sessionData.is_new_user || false,
        trial_left_days: sessionData.trial_left_days || 0,
        lastPaymentCheck: sessionData.lastPaymentCheck || 0,
        borrows_owner_plan: sessionData.borrows_owner_plan === true,
        owner_plans: sessionData.owner_plans || [],
        owner_top_plan: sessionData.owner_top_plan || "",
      };
    } catch {
      return null;
    }
  }, []);

  const handlePaymentVerificationFailure = useCallback(
    (error: any) => {
      const isPaymentError =
        error?.response?.status === 402 ||
        error?.response?.data?.message?.includes("payment") ||
        error?.response?.data?.message?.includes("subscription");

      if (isPaymentError) {
        SecureStorage.clearAuthData();
        auth.logout();

        navigate("/login?payment-expired=true");
        toast.error(t("dashboard.payment.subscription_expired"));
      } else {
        toast.error(
          error?.response?.data?.message ||
            error?.message ||
            t("dashboard.payment.verification_failed")
        );
      }
    },
    [auth, navigate, t]
  );

  const updateUserWithPaymentStatus = useCallback(
    (paymentData: any) => {
      const currentSessionData = SecureStorage.getAuthServiceData();
      if (!currentSessionData) return;

      try {
        const updatedData = {
          ...currentSessionData,
          customer_plans: paymentData.customer_plans || [],
          customer_top_plan: paymentData.customer_top_plan || null,
          is_new_user: paymentData.is_new_user || false,
          trial_left_days: paymentData.trial_left_days || 0,
          is_promo_trial: paymentData.is_promo_trial || false,
          trial_restrictions: paymentData.trial_restrictions,
          trial_end_date: paymentData.trial_end_date,
          lastPaymentCheck: Date.now(),
          hasValidSubscription: (paymentData.customer_plans || []).length > 0,
          borrows_owner_plan: paymentData.borrows_owner_plan === true,
          owner_plans: paymentData.owner_plans || [],
          owner_top_plan: paymentData.owner_top_plan || "",
        };

        SecureStorage.updateAuthData(updatedData);

        auth.setUser({
          ...auth.user,
          ...updatedData,
          exp:
            typeof updatedData.exp === "string"
              ? updatedData.exp
              : updatedData.exp?.toString() || auth.user.exp,
          isAuthenticated: true,
        });

        const hasActiveSubscription =
          paymentData.borrows_owner_plan === true
            ? (paymentData.owner_plans || []).length > 0
            : (paymentData.customer_plans || []).length > 0;
        auth.onOffUserEndedSubscription(!hasActiveSubscription);
      } catch (error) {
        console.error("Failed to update payment status:", error);
      }
    },
    [auth]
  );

  const verifyAndUpdateStatus = useCallback(async (): Promise<void> => {
    if (!auth.user.isAuthenticated || isVerifying) return;
    if (!auth.user.user_identification || !auth.user.entity_identification) {
      return;
    }

    setIsVerifying(true);

    try {
      const paymentData = await verifyPaymentMutation({
        user_identification: auth.user.user_identification,
        entity_identification: auth.user.entity_identification,
      });

      updateUserWithPaymentStatus(paymentData.data?.element);
    } catch (error) {
      handlePaymentVerificationFailure(error);
    } finally {
      setIsVerifying(false);
    }
  }, [
    auth.user.isAuthenticated,
    auth.user.user_identification,
    auth.user.entity_identification,
    isVerifying,
    verifyPaymentMutation,
    updateUserWithPaymentStatus,
    handlePaymentVerificationFailure,
  ]);

  const forcePaymentCheck = useCallback(async (): Promise<void> => {
    return verifyAndUpdateStatus();
  }, [verifyAndUpdateStatus]);

  return {
    isVerifying,
    verifyAndUpdateStatus,
    forcePaymentCheck,
    isPaymentStatusStale,
    getPaymentStatus,
  };
};

export default usePaymentVerification;
